import Dexie, { type EntityTable } from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import type {
  Collaborator, Command, Comment, Filter, FocusSession, Habit, HabitCheckin, Label, Membership, Project, Reminder, Section, Task, User,
} from "@todoer/shared";

/**
 * Local-first store. Every screen reads from here, never from the network.
 * The sync client keeps it in step with the server.
 */
export interface OutboxRow { seq?: number; command: Command; createdAt: number }
export interface MetaRow { key: string; value: unknown }

export const db = new Dexie("todoer") as Dexie & {
  projects: EntityTable<Project, "id">;
  sections: EntityTable<Section, "id">;
  tasks: EntityTable<Task, "id">;
  labels: EntityTable<Label, "id">;
  comments: EntityTable<Comment, "id">;
  filters: EntityTable<Filter, "id">;
  reminders: EntityTable<Reminder, "id">;
  habits: EntityTable<Habit, "id">;
  habitCheckins: EntityTable<HabitCheckin, "id">;
  focusSessions: EntityTable<FocusSession, "id">;
  outbox: EntityTable<OutboxRow, "seq">;
  meta: EntityTable<MetaRow, "key">;
};

db.version(1).stores({
  projects: "id, parentId, isInbox",
  sections: "id, projectId",
  tasks: "id, projectId, sectionId, parentId, completedAt, due.date",
  labels: "id",
  outbox: "++seq",
  meta: "key",
});

db.version(2).stores({
  comments: "id, taskId, projectId",
  filters: "id",
  reminders: "id, taskId",
  habits: "id",
  habitCheckins: "id, habitId, date, [habitId+date]",
  focusSessions: "id, taskId, startedAt",
}).upgrade(async (tx) => {
  // New entity types exist on the server now: fetch everything once.
  await tx.table("meta").delete("syncToken");
});

/** Tables that hold synced entities, in the order they are cleared and filled. */
export const entityTables = () => [
  db.projects, db.sections, db.tasks, db.labels, db.comments, db.filters, db.reminders, db.habits, db.habitCheckins, db.focusSessions,
] as const;

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}
export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}
export const getUser = () => getMeta<User>("user");

export function useUser(): User | undefined {
  return useLiveQuery(() => getMeta<User>("user"), []);
}
export function useCollaborators(): Collaborator[] {
  return useLiveQuery(() => getMeta<Collaborator[]>("collaborators"), [], undefined) ?? [];
}
export function useMemberships(): Membership[] {
  return useLiveQuery(() => getMeta<Membership[]>("memberships"), [], undefined) ?? [];
}

export async function clearLocalData(): Promise<void> {
  await db.transaction("rw", [...entityTables(), db.outbox, db.meta], async () => {
    await Promise.all([...entityTables(), db.outbox, db.meta].map((t) => t.clear()));
  });
}
